import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { MapPin, Waves, ChevronRight, Compass } from "lucide-react";
import { api } from "@/lib/apiClient";
import Seo from "@/components/Seo";
import Breadcrumbs from "@/components/Breadcrumbs";
import { spotPath, regionPath, beginnerPath } from "@/lib/seo";

export default function CountryHub() {
  const { country } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    setErr("");
    api
      .get(`/seo/countries/${country}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.status === 404 ? "not-found" : "error"));
  }, [country]);

  useEffect(() => {
    if (err === "not-found") navigate("/surf-spots", { replace: true });
  }, [err, navigate]);

  if (!data) {
    return <div className="max-w-5xl mx-auto p-8 text-muted-foreground">Loading…</div>;
  }

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: `Surf spots in ${data.country}`,
    description: `Surf areas in ${data.country} across ${data.regions.length} regions.`,
  };

  return (
    <div className="max-w-5xl mx-auto p-6 md:p-8" data-testid="country-hub">
      <Seo
        title={`Surf spots in ${data.country}`}
        description={`Every published surf area in ${data.country} — ${data.spot_count} spots across ${data.regions.length} regions. Suitability, wave conditions, safety and community reports.`}
        path={`/surf-spots/${data.slug}`}
        jsonLd={jsonLd}
      />
      <Breadcrumbs
        items={[
          { label: "Home", to: "/" },
          { label: "Surf spots", to: "/surf-spots" },
          { label: data.country },
        ]}
      />

      <header className="mb-8">
        <h1
          className="text-3xl md:text-5xl font-semibold tracking-tight"
          data-testid="country-heading"
        >
          Surf spots in {data.country}
        </h1>
        <p className="text-muted-foreground mt-3 max-w-2xl text-base md:text-lg">
          {data.spot_count} curated {data.spot_count === 1 ? "spot" : "spots"} across{" "}
          {data.regions.length} {data.regions.length === 1 ? "region" : "regions"} —
          each with personalised suitability scoring, live forecasts and community
          confidence.
        </p>
      </header>

      <section className="mb-10">
        <h2 className="text-lg md:text-xl font-medium mb-4">Regions</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.regions.map((r) => (
            <div
              key={r.slug}
              data-testid={`region-card-${r.slug}`}
              className="rounded-2xl border bg-card hover:bg-accent transition-colors p-5"
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary/10 grid place-items-center text-primary shrink-0">
                  <Compass className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <Link
                    to={regionPath(data.country, r.region)}
                    className="font-medium text-lg hover:underline underline-offset-4"
                  >
                    {r.region}
                  </Link>
                  <div className="text-sm text-muted-foreground mt-1 flex items-center gap-1">
                    <Waves className="w-3.5 h-3.5" />
                    {r.spot_count} spot{r.spot_count === 1 ? "" : "s"}
                    <ChevronRight className="w-3.5 h-3.5 ml-1 opacity-60" />
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Link
                      to={beginnerPath(data.country, r.region)}
                      className="text-xs px-2 py-1 rounded-full bg-secondary text-secondary-foreground hover:bg-secondary/80"
                      data-testid={`beginner-link-${r.slug}`}
                    >
                      Beginner surf →
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-lg md:text-xl font-medium mb-4">Featured spots</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          {data.spots.slice(0, 12).map((s) => (
            <Link
              key={s.id}
              to={spotPath(s)}
              data-testid={`spot-card-${s.slug}`}
              className="group rounded-xl border bg-card hover:bg-accent transition-colors p-4"
            >
              <div className="font-medium group-hover:underline underline-offset-4">
                {s.name}
              </div>
              <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                <MapPin className="w-3 h-3" /> {s.region}
                {s.break_type ? <span className="ml-2">• {s.break_type} break</span> : null}
              </div>
              {s.short_description ? (
                <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                  {s.short_description}
                </p>
              ) : null}
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
